import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { projectApi, Project } from '../services/projectApi';
import {
  DocumentTextIcon, Squares2X2Icon, PhotoIcon,
  WrenchIcon, ArrowRightIcon, PlusIcon,
  ClockIcon, DocumentArrowUpIcon
} from '@heroicons/react/24/outline';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [recentProjects, setRecentProjects] = useState<Project[]>([]);

  useEffect(() => {
    projectApi.getProjects({ page_size: 5 }).then((res: any) => {
      setRecentProjects(res.data?.projects || []);
    }).catch(() => {});
  }, []);

  const quickActions = [
    {
      icon: <DocumentTextIcon className="w-5 h-5" />,
      title: '智能编写',
      desc: 'AI 生成标书内容',
      route: '/projects',
      gradient: 'from-blue-500 to-blue-600',
    },
    {
      icon: <Squares2X2Icon className="w-5 h-5" />,
      title: '模板库',
      desc: '管理文档模板',
      route: '/templates',
      gradient: 'from-emerald-500 to-emerald-600',
    },
    {
      icon: <PhotoIcon className="w-5 h-5" />,
      title: '媒体库',
      desc: '图片与资源管理',
      route: '/media',
      gradient: 'from-purple-500 to-purple-600',
    },
    {
      icon: <WrenchIcon className="w-5 h-5" />,
      title: '工具箱',
      desc: '模型与搜索配置',
      route: '/models',
      gradient: 'from-orange-500 to-orange-600',
    },
  ];

  return (
    <div>
      {/* 问候语 */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">
          你好，{user?.username || '用户'}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          欢迎使用 小灵标 · AI 标书智能编写平台
        </p>
      </div>

      {/* 快捷操作 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {quickActions.map((action, i) => (
          <button
            key={i}
            onClick={() => navigate(action.route)}
            className="card p-0 overflow-hidden text-left group hover:shadow-card-hover transition-shadow"
          >
            <div className="p-5">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${action.gradient} flex items-center justify-center text-white mb-3`}>
                {action.icon}
              </div>
              <h3 className="font-semibold text-foreground text-sm">{action.title}</h3>
              <p className="text-xs text-muted-foreground mt-1">{action.desc}</p>
            </div>
          </button>
        ))}
      </div>

      {/* 最近项目 + 快捷入口 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 最近项目 */}
        <div className="lg:col-span-2 card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-foreground flex items-center gap-2">
              <ClockIcon className="w-4 h-4 text-muted-foreground" />
              最近项目
            </h2>
            <Link to="/projects" className="text-xs text-primary font-medium hover:underline flex items-center gap-1">
              查看全部 <ArrowRightIcon className="w-3 h-3" />
            </Link>
          </div>
          {recentProjects.length === 0 ? (
            <div className="text-center py-12">
              <DocumentArrowUpIcon className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">暂无项目</p>
              <button
                onClick={() => navigate('/projects/new')}
                className="btn-primary mt-4 text-sm"
              >
                创建第一个项目
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {recentProjects.map((proj) => (
                <Link
                  key={proj.id}
                  to={`/projects/${proj.id}`}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-accent transition-colors text-decoration-none"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <DocumentTextIcon className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium truncate">{proj.project_name}</span>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0 ml-2">
                    {proj.status === 'completed' ? '已完成' : proj.status === 'generating' ? '生成中' : '草稿'}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* 快速入口 */}
        <div className="card">
          <h2 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <PlusIcon className="w-4 h-4 text-muted-foreground" />
            快速入口
          </h2>
          <div className="space-y-1">
            {[
              { label: '新建项目', path: '/projects/new' },
              { label: '上传模板', path: '/templates/new' },
              { label: '上传图片', path: '/media' },
              { label: '模型配置', path: '/models' },
              { label: '用户提示词', path: '/user/prompts' },
            ].map((item, i) => (
              <Link
                key={i}
                to={item.path}
                className="flex items-center justify-between p-2.5 rounded-lg hover:bg-accent transition-colors text-sm text-muted-foreground hover:text-foreground"
                style={{ textDecoration: 'none' }}
              >
                {item.label}
                <ArrowRightIcon className="w-3 h-3" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
